<template>
  <div class="audit-manager">
    <div class="audit-manager__heading">
      <a-button type="text" @click="$router.push('/dashboard')"><arrow-left-outlined /></a-button>
      <div>
        <h2>Audit Data Manager</h2>
        <span>Local CloudStack simulator data</span>
      </div>
    </div>

    <a-tabs v-model:activeKey="activeResource" @change="changeResource">
      <a-tab-pane v-for="resource in resourceOptions" :key="resource.key" :tab="resource.label" />
    </a-tabs>

    <a-row :gutter="16">
      <a-col :span="showEditor ? 16 : 24">
        <a-card>
          <template #title>
            <a-space>
              <database-outlined />
              <span>{{ activeDefinition.label }}</span>
              <a-tag>{{ filteredItems.length }}</a-tag>
            </a-space>
          </template>
          <template #extra>
            <a-space wrap>
              <a-select v-model:value="selectedRegion" style="width: 150px">
                <a-select-option v-for="region in regions" :key="region.id" :value="region.id">
                  {{ region.name }}
                </a-select-option>
              </a-select>
              <a-button @click="loadData"><reload-outlined /> Refresh</a-button>
              <a-button @click="exportCsv"><download-outlined /> Export CSV</a-button>
              <a-button @click="openImport"><upload-outlined /> Import CSV</a-button>
              <input ref="csvInput" class="audit-manager__file-input" type="file" accept=".csv,text/csv" @change="importCsv" />
              <a-button type="primary" @click="createItem"><plus-outlined /> Add {{ activeDefinition.singular }}</a-button>
            </a-space>
          </template>

          <a-alert
            type="info"
            show-icon
            message="CSV import updates matching IDs and creates rows whose ID is blank or new."
            style="margin-bottom: 16px" />

          <a-table
            row-key="id"
            :columns="columns"
            :data-source="filteredItems"
            :loading="loading"
            :scroll="{ x: 900 }"
            :pagination="{ pageSize: 10 }">
            <template #bodyCell="{ column, text, record }">
              <template v-if="column.key === 'state'">
                <a-badge :status="['Running', 'Ready', 'enabled', 'Up'].includes(text) ? 'success' : 'default'" :text="text" />
              </template>
              <template v-else-if="column.key === 'isReady' || column.key === 'bootable'">
                <a-tag :color="text ? 'green' : 'default'">{{ text ? 'Yes' : 'No' }}</a-tag>
              </template>
              <template v-else-if="column.key === 'actions'">
                <a-space>
                  <a-button size="small" @click="editItem(record)">Edit</a-button>
                  <a-popconfirm title="Delete this data?" @confirm="deleteItem(record.id)">
                    <a-button size="small" danger>Delete</a-button>
                  </a-popconfirm>
                </a-space>
              </template>
            </template>
          </a-table>
        </a-card>
      </a-col>

      <a-col v-if="showEditor" :span="8">
        <a-card :title="form.id ? `Edit ${activeDefinition.singular}` : `Add ${activeDefinition.singular}`">
          <template #extra><a-button type="text" @click="showEditor = false">Close</a-button></template>
          <a-form layout="vertical">
            <a-form-item v-for="field in activeDefinition.fields" :key="field.key" :label="field.label" :required="field.required">
              <a-switch v-if="field.type === 'boolean'" v-model:checked="form[field.key]" />
              <a-input-number
                v-else-if="field.type === 'number'"
                v-model:value="form[field.key]"
                :min="field.min || 0"
                style="width: 100%" />
              <a-select v-else-if="field.options" v-model:value="form[field.key]">
                <a-select-option v-for="option in field.options" :key="option" :value="option">{{ option }}</a-select-option>
              </a-select>
              <a-input v-else v-model:value="form[field.key]" />
            </a-form-item>
            <a-button type="primary" html-type="button" block :loading="saving" @click="saveItem">Save</a-button>
          </a-form>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
const textField = (key, label, required = true, options = null) => ({
  key,
  label,
  type: 'text',
  required,
  options
})

const numberField = (key, label, min = 0) => ({
  key,
  label,
  type: 'number',
  required: true,
  min
})

const booleanField = (key, label) => ({
  key,
  label,
  type: 'boolean',
  required: false
})

const definitions = {
  instances: {
    label: 'Instances',
    singular: 'Instance',
    fields: [
      textField('regionId', 'Region'),
      textField('name', 'Name'),
      textField('state', 'State', true, ['Running', 'Stopped']),
      textField('internalName', 'Internal name'),
      textField('ipAddress', 'IP address'),
      textField('arch', 'Architecture'),
      textField('host', 'Host'),
      textField('account', 'Account'),
      textField('domainId', 'Domain ID'),
      textField('domain', 'Domain'),
      textField('domainPath', 'Domain path'),
      textField('osType', 'OS type'),
      textField('template', 'Template'),
      numberField('cpu', 'CPU', 1),
      numberField('memoryMb', 'Memory (MiB)', 128)
    ],
    columns: ['name', 'state', 'ipAddress', 'osType', 'host', 'account'],
    defaults: {
      state: 'Running',
      arch: 'x86_64',
      osType: 'Rocky Linux 9',
      template: 'Rocky Linux 9',
      cpu: 2,
      memoryMb: 4096
    }
  },
  volumes: {
    label: 'Volumes',
    singular: 'Volume',
    fields: [
      textField('regionId', 'Region'),
      textField('name', 'Name'),
      textField('state', 'State', true, ['Ready', 'Allocated']),
      numberField('sizeGb', 'Size (GiB)', 1),
      textField('type', 'Type', true, ['ROOT', 'DATADISK']),
      textField('vmName', 'Instance name', false),
      textField('vmState', 'Instance state', false),
      textField('storage', 'Storage'),
      textField('account', 'Account'),
      textField('zoneId', 'Zone ID'),
      textField('zoneName', 'Zone name')
    ],
    columns: ['name', 'state', 'sizeGb', 'type', 'vmName', 'storage', 'account', 'zoneName'],
    defaults: {
      state: 'Ready',
      sizeGb: 50,
      type: 'DATADISK',
      vmName: '',
      vmState: '',
      storage: 'Primary Storage',
      zoneId: '25361055-6c9e-4f7d-8aea-a70ae04c4caa',
      zoneName: 'Zone WJV'
    }
  },
  templates: {
    label: 'Templates',
    singular: 'Template',
    fields: [
      textField('regionId', 'Region'),
      textField('name', 'Name'),
      booleanField('isReady', 'Ready'),
      textField('osType', 'OS type'),
      textField('arch', 'Architecture'),
      textField('hypervisor', 'Hypervisor'),
      numberField('sizeGb', 'Size (GiB)', 0),
      textField('account', 'Account'),
      textField('templateType', 'Template type'),
      textField('displayText', 'Display text')
    ],
    columns: ['name', 'isReady', 'osType', 'arch', 'hypervisor', 'sizeGb', 'account', 'templateType'],
    defaults: {
      isReady: true,
      arch: 'x86_64',
      hypervisor: 'KVM',
      sizeGb: 10,
      account: 'admin',
      templateType: 'USER'
    }
  },
  isos: {
    label: 'ISOs',
    singular: 'ISO',
    fields: [
      textField('regionId', 'Region'),
      textField('name', 'Name'),
      booleanField('isReady', 'Ready'),
      textField('osType', 'OS type'),
      textField('arch', 'Architecture'),
      numberField('sizeGb', 'Size (GiB)', 0),
      textField('account', 'Account'),
      booleanField('bootable', 'Bootable'),
      textField('displayText', 'Display text')
    ],
    columns: ['name', 'isReady', 'osType', 'arch', 'sizeGb', 'account', 'bootable'],
    defaults: {
      isReady: true,
      arch: 'x86_64',
      sizeGb: 2,
      account: 'admin',
      bootable: true
    }
  },
  accounts: {
    label: 'Accounts',
    singular: 'Account',
    fields: [
      textField('regionId', 'Region'),
      textField('name', 'Name'),
      textField('state', 'State', true, ['enabled', 'disabled', 'locked']),
      textField('roleName', 'Role name'),
      textField('roleType', 'Role type', true, ['Admin', 'DomainAdmin', 'User']),
      textField('domainId', 'Domain ID'),
      textField('domain', 'Domain'),
      textField('domainPath', 'Domain path')
    ],
    columns: ['name', 'state', 'roleName', 'roleType', 'domainPath'],
    defaults: {
      state: 'enabled',
      roleName: 'User',
      roleType: 'User',
      domainPath: 'ROOT'
    }
  }
}

const titleFor = key => definitions.instances.fields
  .concat(definitions.volumes.fields, definitions.templates.fields, definitions.isos.fields, definitions.accounts.fields)
  .find(field => field.key === key)?.label || key

export default {
  name: 'ManageDataAudit',
  data () {
    return {
      activeResource: 'instances',
      loading: false,
      saving: false,
      showEditor: false,
      regions: [],
      items: [],
      selectedRegion: window.localStorage.getItem('audit-region') || 'west-java',
      form: {}
    }
  },
  computed: {
    resourceOptions () {
      return Object.entries(definitions).map(([key, definition]) => ({
        key,
        label: definition.label
      }))
    },
    activeDefinition () {
      return definitions[this.activeResource]
    },
    columns () {
      return [
        ...this.activeDefinition.columns.map(key => ({
          title: titleFor(key),
          dataIndex: key,
          key
        })),
        {
          title: 'Actions',
          key: 'actions',
          width: 140,
          fixed: 'right'
        }
      ]
    },
    filteredItems () {
      return this.items.filter(item => item.regionId === this.selectedRegion)
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    endpoint (extra = '') {
      return `/manage-data?resource=${encodeURIComponent(this.activeResource)}${extra}`
    },
    async loadData () {
      this.loading = true
      try {
        const response = await fetch(this.endpoint())
        const data = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)
        this.regions = data.regions || []
        this.items = data[this.activeResource] || []
      } catch (error) {
        this.$message.error(`Unable to load local data: ${error.message}`)
      } finally {
        this.loading = false
      }
    },
    changeResource () {
      this.showEditor = false
      this.items = []
      this.loadData()
    },
    emptyForm () {
      return {
        id: '',
        regionId: this.selectedRegion,
        ...this.activeDefinition.defaults
      }
    },
    createItem () {
      this.form = this.emptyForm()
      this.showEditor = true
    },
    editItem (record) {
      this.form = { ...record }
      this.showEditor = true
    },
    validateForm () {
      const missing = this.activeDefinition.fields.find(field => {
        if (!field.required) return false
        return field.type === 'text' && !String(this.form[field.key] || '').trim()
      })
      if (missing) {
        this.$message.error(`${missing.label} is required`)
        return false
      }
      return true
    },
    async saveItem () {
      if (!this.validateForm()) return
      this.saving = true
      try {
        const response = await fetch(this.endpoint(), {
          method: this.form.id ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.form)
        })
        const result = await response.json().catch(() => ({}))
        if (!response.ok || result.success === false) {
          throw new Error(result.error || `HTTP ${response.status}`)
        }
        this.$message.success(`${this.activeDefinition.singular} saved`)
        this.showEditor = false
        await this.loadData()
      } catch (error) {
        this.$message.error(`Unable to save data: ${error.message}`)
      } finally {
        this.saving = false
      }
    },
    async deleteItem (id) {
      try {
        const response = await fetch(this.endpoint(`&id=${encodeURIComponent(id)}`), {
          method: 'DELETE'
        })
        const result = await response.json().catch(() => ({}))
        if (!response.ok || result.success === false) {
          throw new Error(result.error || `HTTP ${response.status}`)
        }
        this.$message.success(`${this.activeDefinition.singular} deleted`)
        await this.loadData()
      } catch (error) {
        this.$message.error(`Unable to delete data: ${error.message}`)
      }
    },
    csvFields () {
      return ['id', ...this.activeDefinition.fields.map(field => field.key)]
    },
    csvCell (value) {
      const text = value === null || value === undefined ? '' : String(value)
      return `"${text.replace(/"/g, '""')}"`
    },
    exportCsv () {
      const fields = this.csvFields()
      const lines = [
        fields.map(this.csvCell).join(','),
        ...this.filteredItems.map(item => fields.map(field => this.csvCell(item[field])).join(','))
      ]
      const blob = new Blob([`\ufeff${lines.join('\r\n')}`], {
        type: 'text/csv;charset=utf-8'
      })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${this.activeResource}-${this.selectedRegion}.csv`
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(link.href)
      link.remove()
    },
    openImport () {
      this.$refs.csvInput.value = ''
      this.$refs.csvInput.click()
    },
    parseCsv (text) {
      const rows = []
      let row = []
      let value = ''
      let quoted = false
      for (let index = 0; index < text.length; index++) {
        const character = text[index]
        if (quoted && character === '"' && text[index + 1] === '"') {
          value += '"'
          index++
        } else if (character === '"') {
          quoted = !quoted
        } else if (character === ',' && !quoted) {
          row.push(value)
          value = ''
        } else if ((character === '\n' || character === '\r') && !quoted) {
          if (character === '\r' && text[index + 1] === '\n') index++
          row.push(value)
          if (row.some(cell => cell !== '')) rows.push(row)
          row = []
          value = ''
        } else {
          value += character
        }
      }
      row.push(value)
      if (row.some(cell => cell !== '')) rows.push(row)
      return rows
    },
    typedCsvValue (field, value) {
      const definition = this.activeDefinition.fields.find(item => item.key === field)
      if (!definition) return value
      if (definition.type === 'number') return Number(value || 0)
      if (definition.type === 'boolean') return ['1', 'true', 'yes', 'ready'].includes(String(value).toLowerCase())
      return value
    },
    async importCsv (event) {
      const file = event.target.files?.[0]
      if (!file) return
      this.loading = true
      try {
        const rows = this.parseCsv((await file.text()).replace(/^\ufeff/, ''))
        if (rows.length < 2) throw new Error('CSV has no data rows')
        const headers = rows[0].map(header => header.trim())
        if (!headers.includes('name')) throw new Error('CSV must contain a name column')
        const allowed = new Set(this.csvFields())
        const items = rows.slice(1).map(row => {
          const item = {}
          headers.forEach((header, index) => {
            if (allowed.has(header)) item[header] = this.typedCsvValue(header, row[index] || '')
          })
          item.regionId = item.regionId || this.selectedRegion
          return item
        }).filter(item => item.name)
        const response = await fetch(this.endpoint('&action=import'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        })
        const result = await response.json().catch(() => ({}))
        if (!response.ok || result.success === false) {
          throw new Error(result.error || `HTTP ${response.status}`)
        }
        this.$message.success(`Imported ${result.count} ${this.activeDefinition.label}`)
        await this.loadData()
      } catch (error) {
        this.$message.error(`Unable to import CSV: ${error.message}`)
      } finally {
        this.loading = false
        event.target.value = ''
      }
    }
  }
}
</script>

<style lang="less" scoped>
.audit-manager {
  padding: 0 16px 24px;

  &__heading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 20px 16px 4px;

    h2 {
      margin-bottom: 2px;
    }
  }

  &__file-input {
    display: none;
  }
}
</style>
